import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Link, ChevronRight, Award } from 'lucide-react';
import { useVendorService } from '../../services/vendorService';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { commissionService } from '../../services/commissionService';
import { getPlatformSettlementCurrency } from '../../services/settlementCurrencyService';
import { useMarketByCountry } from '../../services/marketService';
import { useTaxQuote } from '../../services/taxService';
import {
  resolvePaymentTerms,
  calculatePaymentAmount,
  rulesForPaymentCalculation,
} from '../../utils/paymentTerms';
import {
  BookingHeader,
  BookingProgressSteps,
  PackageSelection,
  EventDetailsForm,
  ContactInfoForm,
  ReviewPayment,
  BookingSidebar,
  MobileBookingSidebar,
  NavigationButtons
} from '../../components/client/booking';

const ClientBooking = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [bookingData, setBookingData] = useState({
    eventDate: '',
    eventTime: '',
    duration: '4',
    guestCount: '',
    eventType: '',
    location: '',
    eventCountry: '',
    billingCountry: '',
    customerType: 'individual',
    customerTaxId: '',
    specialRequests: '',
    contactInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: ''
    },
    selectedPackage: null
  });
  // Removed createdBookingId - booking will be created after payment succeeds
  const [cardholderName, setCardholderName] = useState('');
  const [errors, setErrors] = useState({});
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState(null);

  const { user } = useAuth();
  const { data: serviceResponse, isLoading, error } = useVendorService(id);
  
  // Fetch default commission setting
  const { data: commissionResponse } = useQuery({
    queryKey: ['defaultCommissionSetting'],
    queryFn: () => commissionService.getDefault(),
    enabled: true,
    retry: 1,
  });

  const commissionSetting = commissionResponse?.data;

  const { data: settlement } = useQuery({
    queryKey: ['platformSettlementCurrency'],
    queryFn: () => getPlatformSettlementCurrency(),
    staleTime: 60_000,
  });
  const displayCurrency = settlement?.display_currency || settlement?.currency || 'USD';
  const displayLocale = settlement?.display_locale || 'en-US';

  const marketCountryCode =
    bookingData.eventCountry || bookingData.billingCountry || '';
  const { data: marketResponse } = useMarketByCountry(marketCountryCode);
  const activeMarket = marketResponse?.data || null;

  const subtotalForTax = (() => {
    if (!bookingData.selectedPackage) return 0;
    let subtotal = Number(bookingData.selectedPackage.price || 0);
    const packageHours = parseInt(bookingData.selectedPackage.duration_hours || 4);
    const requestedHours = parseInt(bookingData.duration);
    if (requestedHours > packageHours) {
      subtotal += (requestedHours - packageHours) * 50;
    }
    const discount = appliedDiscount?.discount_amount ?? 0;
    return Math.max(0, subtotal - discount);
  })();

  const { data: taxQuoteResponse } = useTaxQuote({
    subtotal: subtotalForTax,
    currency: displayCurrency || 'USD',
    event_country: bookingData.eventCountry || null,
    billing_country: bookingData.billingCountry || null,
    customer_type: bookingData.customerType || 'individual',
    customer_tax_id: bookingData.customerTaxId || null,
  });

  const taxQuoteData = taxQuoteResponse?.data;
  const showTaxLine = Boolean(
    taxQuoteData?.show_tax_line ?? activeMarket?.tax_collect_enabled
  );
  const taxAmount = Number(taxQuoteData?.quote?.tax_amount ?? 0) || 0;
  const marketMeta = marketResponse?.meta || {};
  const requiredCustomerFields = Array.isArray(activeMarket?.required_customer_fields)
    ? activeMarket.required_customer_fields
    : [];
  const marketBookable = !activeMarket
    ? true
    : Boolean(
        marketMeta.is_bookable ??
          (activeMarket.status === 'active' && activeMarket.bookings_enabled !== false)
      );
  const requiresField = (field) => requiredCustomerFields.includes(field);
  
  // Removed booking creation - booking will be created after payment succeeds
  // console.log(serviceResponse);
  const service = serviceResponse?.data;
  const vendor = service?.vendor_profile;
  const category = service?.service_category;
  const serviceRating = service?.average_rating || 0;
  const serviceReviews = service?.total_reviews || 0;
  
  const packages = service?.vendor_service_packages?.map((pkg) => ({
    id: pkg.id,
    name: pkg.package_name || 'Package',
    price: pkg.base_price || 0,
    duration: pkg.duration_hours ? `${pkg.duration_hours} hours` : 'Flexible',
    description: pkg.description || 'No description available',
    includes: pkg.includes_items || [],
    duration_hours: pkg.duration_hours || 4,
    payment_schedule_rules: pkg.payment_schedule_rules || null,
  })) || [];

  const getSelectedPackageRules = () => {
    const raw =
      bookingData.selectedPackage?.payment_schedule_rules ??
      service?.vendor_service_packages?.find(
        (p) => p.id === bookingData.selectedPackage?.id
      )?.payment_schedule_rules;
    return rulesForPaymentCalculation(raw);
  };

  const steps = [
    { id: 1, title: 'Package' },
    { id: 2, title: 'Details' },
    { id: 3, title: 'Contact' },
    { id: 4, title: 'Payment' }
  ];

  useEffect(() => {
    if (user && currentStep === 3) {
      const nameParts = (user.name || '').split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      setBookingData(prev => ({
        ...prev,
        contactInfo: {
          firstName: firstName,
          lastName: lastName,
          email: user.email || '',
          phone: user.phone || ''
        }
      }));
    }
  }, [user, currentStep]);

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setBookingData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setBookingData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handlePackageSelect = (packageId) => {
    const selectedPackage = packages.find(pkg => pkg.id === packageId);
    setBookingData(prev => ({
      ...prev,
      selectedPackage,
      duration: selectedPackage?.duration_hours || '4'
    }));
    // Clear error when package is selected
    if (errors.selectedPackage) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.selectedPackage;
        return newErrors;
      });
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!bookingData.selectedPackage) {
        newErrors.selectedPackage = 'Please select a package to continue';
      }
    }
    
    if (step === 2) {
      if (!bookingData.eventDate) {
        newErrors.eventDate = 'Event date is required';
      } else {
        // Match API: event_date must be after today (same-day not allowed)
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const y = today.getFullYear();
        const m = String(today.getMonth() + 1).padStart(2, '0');
        const d = String(today.getDate()).padStart(2, '0');
        const todayStr = `${y}-${m}-${d}`;
        if (bookingData.eventDate <= todayStr) {
          newErrors.eventDate = 'The event date must be a date after today.';
        }
      }
      if (!bookingData.eventTime) {
        newErrors.eventTime = 'Event time is required';
      }
      if (!bookingData.eventType || bookingData.eventType.trim() === '') {
        newErrors.eventType = 'Event type is required';
      }
      if (!bookingData.duration || bookingData.duration === '') {
        newErrors.duration = 'Duration is required';
      }
      if (requiresField('event_country') && !bookingData.eventCountry) {
        newErrors.eventCountry = 'Event market is required for this booking';
      }
      if (requiresField('event_address') && !(bookingData.location || '').trim()) {
        newErrors.location = 'Event address is required for this market';
      }
      if (requiresField('event_location') && !(bookingData.location || '').trim()) {
        newErrors.location = 'Event location is required for this market';
      }
    }
    
    if (step === 3) {
      if (!bookingData.contactInfo.firstName || bookingData.contactInfo.firstName.trim() === '') {
        newErrors['contactInfo.firstName'] = 'First name is required';
      }
      if (!bookingData.contactInfo.lastName || bookingData.contactInfo.lastName.trim() === '') {
        newErrors['contactInfo.lastName'] = 'Last name is required';
      }
      if (!bookingData.contactInfo.email || bookingData.contactInfo.email.trim() === '') {
        newErrors['contactInfo.email'] = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bookingData.contactInfo.email)) {
        newErrors['contactInfo.email'] = 'Please enter a valid email address';
      }
      if (!bookingData.contactInfo.phone || bookingData.contactInfo.phone.trim() === '') {
        newErrors['contactInfo.phone'] = 'Phone number is required';
      }
      if (requiresField('billing_country') && !bookingData.billingCountry) {
        newErrors.billingCountry = 'Billing market is required for this booking';
      }
      if (requiresField('customer_type') && !bookingData.customerType) {
        newErrors.customerType = 'Customer type is required for this market';
      }
      if (
        requiresField('customer_tax_id') &&
        !(bookingData.customerTaxId || '').trim()
      ) {
        newErrors.customerTaxId = 'Tax ID is required for this market';
      }
    }

    if (step === 4) {
      if (!bookingData.eventCountry && !bookingData.billingCountry) {
        newErrors.eventCountry = 'Select an event or billing market so we can apply the right rules.';
      }
      if (activeMarket && !marketBookable) {
        newErrors.market = marketMeta.reason || 'This market is not open for bookings.';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    // Validate current step before proceeding
    if (!validateStep(currentStep)) {
      return; // Don't proceed if validation fails
    }
    
    // Proceed to next step (booking will be created after payment succeeds)
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      setErrors({}); // Clear errors when moving to next step
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const calculateSubtotal = () => {
    if (!bookingData.selectedPackage) return 0;
    let subtotal = Number(bookingData.selectedPackage.price || 0);
    const packageHours = parseInt(bookingData.selectedPackage.duration_hours || 4);
    const requestedHours = parseInt(bookingData.duration);
    if (requestedHours > packageHours) {
      const extraHours = requestedHours - packageHours;
      subtotal += extraHours * 50;
    }
    return subtotal;
  };

  const getSubtotalAfterDiscount = () => {
    const before = calculateSubtotal();
    const discount = appliedDiscount?.discount_amount ?? 0;
    return Math.max(0, before - discount);
  };

  const calculateTax = () => taxAmount;

  const calculateTotal = () => {
    const subtotalAfter = getSubtotalAfterDiscount();
    const commission = calculateCommission();
    return subtotalAfter + commission + calculateTax();
  };

  const calculateEndTime = (startTime, duration) => {
    if (!startTime || !duration) return '';
    const [hours, minutes] = startTime.split(':').map(Number);
    const startDate = new Date();
    startDate.setHours(hours, minutes, 0, 0);
    const endDate = new Date(startDate.getTime() + (parseFloat(duration) * 60 * 60 * 1000));
    return endDate.toTimeString().slice(0, 5);
  };

  const calculatePaymentTerms = () => {
    if (!bookingData.eventDate) {
      return null;
    }
    const subtotal = getSubtotalAfterDiscount();
    const platformFee = calculateCommission();
    const tax = calculateTax();
    return resolvePaymentTerms(
      bookingData.eventDate,
      subtotal,
      getSelectedPackageRules(),
      { platformFee, tax }
    );
  };

  /**
   * Calculate proportional platform fee for a payment based on payment percentage
   * This ensures platform fee is distributed across all installments
   * Each payment includes its proportional share of the platform fee
   */
  const calculateProportionalPlatformFee = (paymentPercentage) => {
    const totalPlatformFee = calculateCommission();
    // Calculate proportional platform fee based on payment percentage
    const proportionalFee = (totalPlatformFee * paymentPercentage) / 100;
    return Math.round(proportionalFee * 100) / 100;
  };

  const calculateDepositAmount = () => {
    const subtotal = getSubtotalAfterDiscount();
    const platformFee = calculateCommission();
    const tax = calculateTax();
    const paymentTerms = calculatePaymentTerms();
    
    if (!paymentTerms) {
      return calculatePaymentAmount(subtotal, platformFee, 50, tax);
    }

    if (paymentTerms.amountDueNow != null) {
      return paymentTerms.amountDueNow;
    }

    return calculatePaymentAmount(subtotal, platformFee, paymentTerms.depositPercentage, tax);
  };

  /**
   * Calculate balance payment amount (remaining amount after deposit)
   * Includes proportional platform fee for the remaining percentage
   */
  const calculateBalanceAmount = () => {
    const subtotal = getSubtotalAfterDiscount();
    const platformFee = calculateCommission();
    const paymentTerms = calculatePaymentTerms();
    
    if (!paymentTerms) {
      return calculatePaymentAmount(subtotal, platformFee, 50);
    }

    if (paymentTerms.schedule?.length) {
      const remaining = paymentTerms.schedule
        .filter((p) => !p.isDueNow)
        .reduce((sum, p) => sum + (p.totalAmount ?? 0), 0);
      return Math.round(remaining * 100) / 100;
    }

    const remainingPercentage = 100 - (paymentTerms.depositPercentage || 50);
    return calculatePaymentAmount(subtotal, platformFee, remainingPercentage);
  };

  const calculateCommission = () => {
    if (!commissionSetting || !commissionSetting.is_active) {
      return 0;
    }
    const subtotal = getSubtotalAfterDiscount();
    if (commissionSetting.commission_type === 'fixed') {
      return parseFloat(commissionSetting.fixed_amount || 0);
    }
    return (subtotal * parseFloat(commissionSetting.commission_rate || 0)) / 100;
  };

  const handleSubmit = () => {
    // Move to payment step - booking will be created after payment succeeds
    if (currentStep !== 4) {
      setCurrentStep(4);
    }
  };

  const handlePaymentSuccess = (paymentData) => {
    // Navigate to success page after successful payment
    // Booking is now created in the backend after payment succeeds
    navigate('/booking-success', {
      state: {
        bookingData: {
          id: paymentData.booking?.id || paymentData.bookingId,
          eventDate: bookingData.eventDate,
          eventTime: bookingData.eventTime,
          duration: bookingData.duration,
          location: bookingData.location,
          guestCount: bookingData.guestCount,
          eventType: bookingData.eventType,
          specialRequests: bookingData.specialRequests,
          selectedPackage: bookingData.selectedPackage,
          totalAmount: calculateTotal(),
          depositAmount: calculateDepositAmount()
        },
        paymentData: paymentData,
        serviceData: {
          service_name: service?.service_name,
          category_name: category?.category_name,
          average_rating: serviceRating,
          total_reviews: serviceReviews
        },
        vendorData: {
          business_name: vendor?.business_name,
          city: vendor?.city,
          state: vendor?.state
        }
      }
    });
  };

  const handlePaymentError = (error) => {
    console.error('Payment failed:', error);
  };

  // Skeleton Loader
  const SkeletonLoader = () => (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <div className="h-6 w-20 bg-gray-200 rounded mb-8 animate-pulse"></div>
          <div className="h-10 w-64 bg-gray-200 rounded mb-4 animate-pulse"></div>
          <div className="h-5 w-48 bg-gray-200 rounded animate-pulse"></div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <div className="h-8 w-32 bg-gray-200 rounded mb-8 animate-pulse"></div>
            <div className="border-2 border-gray-200 rounded-lg p-8">
              <div className="space-y-4">
                <div className="h-6 w-full bg-gray-200 rounded animate-pulse"></div>
                <div className="h-6 w-3/4 bg-gray-200 rounded animate-pulse"></div>
                <div className="h-6 w-full bg-gray-200 rounded animate-pulse"></div>
              </div>
            </div>
          </div>
          <div>
            <div className="border-2 border-gray-200 rounded-lg p-6">
              <div className="h-6 w-32 bg-gray-200 rounded mb-4 animate-pulse"></div>
              <div className="space-y-3">
                <div className="h-4 w-full bg-gray-200 rounded animate-pulse"></div>
                <div className="h-4 w-full bg-gray-200 rounded animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    );

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (error || !service) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Award className="w-8 h-8 text-black" />
          </div>
          <h2 className="text-2xl font-bold text-black mb-3">Service Not Found</h2>
          <p className="text-gray-600 mb-8">The service you're looking for doesn't exist or is no longer available.</p>
          <Link to="/browse" className="inline-flex items-center px-6 py-3 bg-black text-white rounded-md hover:bg-gray-900 transition-colors">
            Browse Services
            <ChevronRight className="w-4 h-4 ml-2" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12">
        <BookingHeader
          serviceId={id}
          service={service}
          vendor={vendor}
          serviceRating={serviceRating}
          serviceReviews={serviceReviews}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <BookingProgressSteps steps={steps} currentStep={currentStep} />

            {/* Step Content */}
            <div>
              {/* Step 1: Select Package */}
              {currentStep === 1 && (
                <PackageSelection 
                  packages={packages}
                  selectedPackage={bookingData.selectedPackage}
                  onSelectPackage={handlePackageSelect}
                  onChangePackage={() =>
                    setBookingData((prev) => ({ ...prev, selectedPackage: null }))
                  }
                  error={errors.selectedPackage}
                />
              )}

              {/* Step 2: Event Details */}
              {currentStep === 2 && (
                <EventDetailsForm
                  bookingData={bookingData}
                  errors={errors}
                  onInputChange={handleInputChange}
                  requiredFields={requiredCustomerFields}
                />
              )}

              {/* Step 3: Contact Info */}
              {currentStep === 3 && (
                <ContactInfoForm
                  bookingData={bookingData}
                  errors={errors}
                  onInputChange={handleInputChange}
                  user={user}
                  requiredFields={requiredCustomerFields}
                />
              )}

              {/* Step 4: Review & Pay */}
              {currentStep === 4 && (
                <>
                  {activeMarket && !marketBookable && (
                    <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                      {marketMeta.reason || 'This market is not open for bookings yet.'}
                      {' '}Activate the market in Admin → Markets, or choose another country.
                    </div>
                  )}
                  {errors.market && (
                    <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                      {errors.market}
                    </div>
                  )}
                <ReviewPayment
                  bookingData={bookingData}
                  vendor={vendor}
                  service={service}
                  calculateSubtotal={calculateSubtotal}
                  getSubtotalAfterDiscount={getSubtotalAfterDiscount}
                  calculateCommission={calculateCommission}
                  calculateTax={calculateTax}
                  calculateTotal={calculateTotal}
                  calculateDepositAmount={calculateDepositAmount}
                  calculateBalanceAmount={calculateBalanceAmount}
                  calculateProportionalPlatformFee={calculateProportionalPlatformFee}
                  calculatePaymentTerms={calculatePaymentTerms}
                  calculateEndTime={calculateEndTime}
                  commissionSetting={commissionSetting}
                  showTaxLine={showTaxLine}
                  activeMarket={activeMarket}
                  appliedDiscount={appliedDiscount}
                  displayCurrency={displayCurrency}
                  displayLocale={displayLocale}
                  onApplyDiscount={setAppliedDiscount}
                  onRemoveDiscount={() => setAppliedDiscount(null)}
                  onRetry={handleSubmit}
                  onPaymentSuccess={handlePaymentSuccess}
                  onPaymentError={handlePaymentError}
                  cardholderName={cardholderName}
                  onCardholderNameChange={setCardholderName}
                />
                </>
              )}

              <NavigationButtons
                currentStep={currentStep}
                totalSteps={steps.length}
                onPrevious={prevStep}
                onNext={nextStep}
                isNextDisabled={currentStep === 1 && !bookingData.selectedPackage}
              />
            </div>
          </div>

          <BookingSidebar
            vendor={vendor}
            category={category}
            service={service}
            serviceRating={serviceRating}
            serviceReviews={serviceReviews}
            bookingData={bookingData}
            calculateSubtotal={calculateSubtotal}
            calculateCommission={calculateCommission}
            calculateTax={calculateTax}
            calculateTotal={calculateTotal}
            calculateDepositAmount={calculateDepositAmount}
            calculatePaymentTerms={calculatePaymentTerms}
            commissionSetting={commissionSetting}
            showTaxLine={showTaxLine}
            appliedDiscount={appliedDiscount}
            displayCurrency={displayCurrency}
            displayLocale={displayLocale}
          />

          <MobileBookingSidebar
            isOpen={showMobileSidebar}
            onClose={() => setShowMobileSidebar(false)}
            onOpen={() => setShowMobileSidebar(true)}
            vendor={vendor}
            category={category}
            service={service}
            serviceRating={serviceRating}
            serviceReviews={serviceReviews}
            bookingData={bookingData}
            calculateSubtotal={calculateSubtotal}
            calculateCommission={calculateCommission}
            calculateTax={calculateTax}
            calculateTotal={calculateTotal}
            calculateDepositAmount={calculateDepositAmount}
            calculatePaymentTerms={calculatePaymentTerms}
            commissionSetting={commissionSetting}
            showTaxLine={showTaxLine}
            appliedDiscount={appliedDiscount}
            displayCurrency={displayCurrency}
            displayLocale={displayLocale}
          />
        </div>
      </div>
    </div>
  );
};

export default ClientBooking;
